/**
 * Single source of truth for app changelog entries. Surfaces in two
 * places:
 *
 *   1. Profile slide-out → "What's New in v5.2.x" link (visible to all
 *      roles). Shows ONLY the latest version's items where
 *      `audience === "everyone"` so admin-only changes don't leak to
 *      crew. **Renders titles only** — keep them BRIEF + FUN since
 *      wow crew just want to know what changed at a glance. The
 *      detailed `detail` paragraphs only render on the Reports tab.
 *      Tiny pulsing dot when the user hasn't seen this version
 *      yet (localStorage tracks `wow_changelog_seen_version`).
 *
 *   2. Reports → "Changelog" tab (admins/managers/DM only). Shows
 *      every version with full detail. Admins see EVERY item;
 *      non-admin managers see `audience !== "admin"` only.
 *
 * Entry shape:
 *   {
 *     version: "5.2.37",
 *     date: "2026-02-14",
 *     headline: "Short one-liner that crew might read",
 *     items: [
 *       { type: "feature"|"fix"|"polish"|"security"|"admin",
 *         title: "Short title",
 *         detail?: "Optional longer explanation for the Reports tab",
 *         audience: "everyone"|"admin" }
 *     ]
 *   }
 *
 * Newest version FIRST. Bump the array when you ship a new build.
 */

export const CHANGELOG = [
  {
    version: "5.2.46",
    date: "2026-02-15",
    headline: "Penny Rounding is here + Home page reorder",
    items: [
      {
        type: "feature",
        title: "Penny Rounding — one-tap discount barcodes 🪙",
        detail: "New tile on Home for every crew member. Tap it → pick the digit the total ends in (1/6, 2/7, 3/8, or 4/9) → full-screen barcode ready to scan. Cash-only reminder pill at the top so nobody rings this on a card sale. ACO and Legacy register steps collapsed in accordions right below so cashiers can peek at the flow if they forget. Barcodes: 773000001–004, matching the corporate rollout doc.",
        audience: "everyone",
      },
      {
        type: "feature",
        title: "Zone Sheet world tasks — one-tap Start & Complete",
        detail: "Wow Crew can now Start (which also acknowledges) and Mark Complete the 4 world tasks (PURGE / RECOVER / PRICE / ADJUST) directly from the 'Your Tasks Today' sheet — no more bouncing to Zone Sheet. Start flips the row to 'In Progress' with an indigo chip; Complete flips it to 'Waiting for manager to verify'. Managers see the same states from their side.",
        audience: "everyone",
      },
      {
        type: "polish",
        title: "Home page tile order rebuilt around the register",
        detail: "New order: Collect Emails → Penny Rounding → Contest (when active) → Zone Sheet → Manager Dashboard → Leaderboards → What's Up → District. Cashier tools sit at the top so they're the fastest tap of the shift.",
        audience: "everyone",
      },
      {
        type: "polish",
        title: "Reports moved to Profile menu 📊",
        detail: "Reports tile is off the Home grid for managers/admins/DMs — now lives in the Profile slide-out alongside Admin Console and Settings. A tiny pulsing red dot appears on the first Reports visit of each MST week so a fresh weekly rollup still catches your eye.",
        audience: "admin",
      },
    ],
  },
  {
    version: "5.2.45",
    date: "2026-02-15",
    headline: "Sales recovery in Store Pulse + Home tiles wake up instantly",
    items: [
      {
        type: "polish",
        title: "Manager Dashboard is way tighter",
        detail: "Full layout pass — Win The Day hero shrunk 37% (330px → 208px), empty-state cards collapse to single-row chips (140px → 58px, so Who's on Floor / Notes / Truck Plan / Tasks don't waste space when there's no data yet), section headers lost the 'Step N' numbering, and the 'Checkpoint Readings Moved' banner only shows now when a slot is actually overdue. Net result: Win The Day + Store Pulse both visible in one mobile viewport instead of two. All existing features preserved — full cards return the moment there's real data.",
        audience: "admin",
      },
      {
        type: "feature",
        title: "Pace to Win — recovery guidance in Store Pulse 🎯",
        detail: "New card in Manager Dashboard → Store Pulse answers 'how much more to hit Plan?' at a glance: dollars remaining, transactions needed, captures needed, and required $/hr for the rest of the day. Includes a next-checkpoint sub-line so you can pace to either close or the next reading. States shift between Winning · One More Push · Game On · Critical Focus so the copy matches the day. Data comes from the same tally poll — no new fetches.",
        audience: "everyone",
      },
      {
        type: "feature",
        title: "Checkpoint Trends — 2PM/5PM/8PM stay locked once saved",
        detail: "The Mission Control card now shows an immutable Checkpoint Trends row for each slot: rate%, sales$, tx count, plus a green/red delta pill vs. the previous slot. Once a checkpoint is saved, its rate is frozen at that moment — no more 'why did 2PM change?' when tally moves later in the day. Reviewer-verified with 50 live tally adds: 2PM stayed at 24.0%.",
        audience: "everyone",
      },
      {
        type: "feature",
        title: "Reset Day — clear Plan/Comp/ADS/UPT without wiping captures",
        detail: "New rose 'Reset day' button in the Eyes on the Biz KPI editor (managers only). Blocking two-step dialog with an explicit warning — outside-click and Escape both blocked so a stray tap can't nuke the day. Clears Plan $, Comp Goal $, ADS, and UPT while PRESERVING every checkpoint capture. Includes an audit stamp so we know who reset it.",
        audience: "admin",
      },
      {
        type: "feature",
        title: "Cover another store in one tap 🔄",
        detail: "Admins and DMs who cover a sister store now get a green 'Cover 3015' pill in the top-right when they're on their home store. One tap re-enters Store Mirror on the last covered store — no more Profile → Admin → Stores → find store → mirror. Remembers your last covered store for 24h; a small × dismisses it for the day.",
        audience: "admin",
      },
      {
        type: "feature",
        title: "First login of the day gets a proper welcome 🌅",
        detail: "Full-screen splash mirrors the goodbye — time-of-day art (sunrise / sky / sunset / stars), a rotating greeting like 'What's up, Dom!' or 'Owl mode!', and yesterday's stat brag ('You captured 12 emails yesterday — top 3!'). Fires once per crew per day, auto-dismisses in 2.6s, tap anywhere to skip. Managers see store capture rate; new hires get a friendly 'Fresh page' line.",
        audience: "everyone",
      },
      {
        type: "fix",
        title: "Home tile wakes up instantly after a scan",
        detail: "Collect Emails tile used to sit 'snoozing' up to a minute after you scanned your barcode — it was polling once every 30 seconds. Now it flips to 'dancing' the instant the scan lands. Manager Dashboard also picks up the change immediately.",
        audience: "everyone",
      },
      {
        type: "polish",
        title: "Daily Passcode barcode is BIG now",
        detail: "'Ready · Let's go' barcode and 'Snoozing' character used to look tiny next to the Tasks half-donut. Scaled both up to match — bars are ~2x taller, character face is bigger, label text sizes up on tablets. Same personality (bored ↔ alive), same colors.",
        audience: "everyone",
      },
      {
        type: "fix",
        title: "Coupon barcode centers properly",
        detail: "In the full-screen coupon sheet, short coupon codes were flush-left instead of centered. Block-level SVG needed auto margins. Fixed for all barcode surfaces (coupon sheet, admin preview, coupon card).",
        audience: "everyone",
      },
      {
        type: "security",
        title: "Backend write endpoints require your login 🔐",
        detail: "Audit found several write endpoints (tap tally, adjust tally, save checkpoint, save Plan/Comp, save weekly figures) were accepting requests without proving the caller was actually logged in as an active crew member. Now every write requires a valid actor and rejects unknown/inactive callers. Includes an extra guard on POST /api/tally — you can only tap for yourself unless you're a manager+.",
        audience: "everyone",
      },
      {
        type: "admin",
        title: "Slimmer backend — 502s should hit less often ⚡",
        detail: "Backend dependency list went from 149 packages to 64 (−57%). Removed the entire ML/AI stack (rembg, scipy, numba, onnxruntime, tokenizers, HF), unused Google/AWS SDKs, Stripe (not implemented), OpenAI/LiteLLM, and every dev tool. Container is meaningfully smaller — cold-start time drops significantly, which is the exact fix for '502 when I open the app fresh'. Requires redeploy to hit production.",
        audience: "admin",
      },
    ],
  },
  {
    version: "5.2.44",
    date: "2026-02-14",
    headline: "Story @mentions actually ping the bell",
    items: [
      {
        type: "fix",
        title: "Story @mentions ping the bell now 🔔",
        detail: "If you tag a teammate from the @mention picker when posting a story, they finally get a real bell notification with your name and a preview. The fix was a wrong-collection write — the row was landing in a place the bell UI never reads from. Now writes to the same place inbox @mentions use, scoped to the recipient's store so cross-store mentions reach 3015/3010/etc. too.",
        audience: "everyone",
      },
      {
        type: "feature",
        title: "Tap a story mention → jumps to the story",
        detail: "Tapping a story-mention row in your notifications panel now opens Home (where the Stories row lives) instead of dumping you in Inbox like the generic DM bucket. One tap from bell to story.",
        audience: "everyone",
      },
      {
        type: "security",
        title: "Story mentions stay between you two 🤫",
        detail: "Managers no longer see the personal \"you were mentioned\" row when it's targeted at someone else. Story-mention rows now live in the same personal-only allowlist as DMs, inbox mentions, story views, and story reactions.",
        audience: "everyone",
      },
      {
        type: "feature",
        title: "Full log link → straight to the changelog",
        detail: "The \"Full log\" link at the bottom of the What's New popover used to drop you on the Reports landing tab. Now it deep-links to Reports → Changelog so you go straight to the version history.",
        audience: "everyone",
      },
      {
        type: "admin",
        title: "Bell-tray pytest now hits the actual HTTP endpoint",
        detail: "Old test asserted against db.notifications_bell (the SSE backfill collection) which is why two prior \"fixes\" looked green but were really still broken. New test posts a story, then GETs /api/notifications as the recipient — the same endpoint HomePage/Manager/District bells call — and asserts the row is there. Adds a privacy assertion (a bystander can't see another crew's mention) and a negative case (no @mention → no rows).",
        audience: "admin",
      },
    ],
  },
  {
    version: "5.2.42",
    date: "2026-02-14",
    headline: "Privacy scrub + frosted drawers + tile compaction",
    items: [
      {
        type: "security",
        title: "Tidied up an internal ID leak 🧹",
        detail: "Real employee IDs that had leaked into the changelog text, test fixtures, and memory docs were replaced with generic placeholders. Going forward we use `123456` as the sample admin ID in any user-facing reference.",
        audience: "everyone",
      },
      {
        type: "polish",
        title: "Frosted-glass Profile + Notifications ❄️",
        detail: "Both slide-outs now sit on a soft 12-16px backdrop blur with a subtle gradient tint instead of the flat white surface. Reads cleaner against busy home backgrounds and matches the iOS Control Center vibe.",
        audience: "everyone",
      },
      {
        type: "polish",
        title: "Home fits on one screen again 📱",
        detail: "Tiles shrank slightly (min-height 112 → 92, gap-2.5 → gap-1.5) so the full home grid renders on a 390×844 iPhone without scrolling. The top-right corner chevron stayed put.",
        audience: "everyone",
      },
      {
        type: "fix",
        title: "Login splash no longer leaks store stats",
        detail: "Pre-login splash ticker no longer pulls from /api/login/feed. It rotates three hardcoded brand quotes (\"Let go and have fun\", \"Every email is a future fan\", \"Colorado West — Mountain Region\") with no store, district, or email counts visible.",
        audience: "everyone",
      },
      {
        type: "admin",
        title: "Bug report email diagnostics",
        detail: "Per-recipient send/skipped/error trace on every ticket-opened email with env-flag context (resend_key_set, admin_email_set) so missing-email symptoms can be triaged from production logs without a redeploy.",
        audience: "admin",
      },
    ],
  },
  {
    version: "5.2.38",
    date: "2026-02-14",
    headline: "Classic layout restored + login ticker scrubbed",
    items: [
      {
        type: "polish",
        title: "Home + Login back to the classic look",
        detail: "Reverted the deep-blue blueprint redesign on Home and Login. The classic soft-tinted home with rounded white tile cards + deep-blue gradient login with frosted card is back as the default.",
        audience: "everyone",
      },
      {
        type: "security",
        title: "Login ticker no longer exposes operational data",
        detail: "The morale ticker on the login splash used to pull `/api/login/feed` which mixed in store / district / email counts. Pre-login surfaces shouldn't leak any of that — the ticker now shows only hardcoded brand quotes (\"Let go and have fun\", \"Every email is a future fan\", \"Colorado West — Mountain Region\").",
        audience: "everyone",
      },
      {
        type: "feature",
        title: "Blueprint saved as a theme",
        detail: "The deep-blue blueprint look isn't gone — it's saved as a \"Blueprint\" option in your Settings theme picker, alongside Halloween / Midnight / Golden Dumpling. Tap it to flip your color palette to spec-sheet blue.",
        audience: "everyone",
      },
      {
        type: "admin",
        title: "Blueprint layout swap pending wire-up",
        detail: "The Blueprint theme currently only swaps colors, not the full grid+registration-mark layout. The CSS for the full layout swap is preserved in /styles/blueprint.css and gated behind `theme.layout === 'blueprint'` for future wire-up if you want the full skin back later.",
        audience: "admin",
      },
    ],
  },
  {
    version: "5.2.37",
    date: "2026-02-14",
    headline: "Daily Passcode regex fix + stale-receipt lock",
    items: [
      {
        type: "fix",
        title: "Daily Passcode accepts real receipt format",
        detail: "Real Five Below receipt codes (6 digits + mixed alphanumerics + slash + alphanumerics, e.g. 123456ABC0/XYZWVUTSR) were being rejected by an overly-strict regex. Lenient fallback added + Code 128 hidden control characters now stripped before validation.",
        audience: "everyone",
      },
      {
        type: "security",
        title: "Can't override the OCR's stale-date detection",
        detail: "If the camera reads a date from your printed receipt that isn't today, the Claim button is now hard-blocked. Previously the typed-date input could be overwritten with today's date to bypass the check. Now the only path forward is to print a fresh passcode and re-scan.",
        audience: "everyone",
      },
      {
        type: "feature",
        title: "What's New in your profile",
        detail: "Tap your avatar → see what changed in this release. Admins also get a Changelog tab in Reports with the full history.",
        audience: "everyone",
      },
      {
        type: "fix",
        title: "Daily Passcode date entry easier",
        detail: "Receipt date field is now two separate Month / Day numeric inputs so the iOS keypad (which has no slash) works cleanly.",
        audience: "everyone",
      },
      {
        type: "feature",
        title: "OCR auto-reads your receipt date",
        detail: "Camera scan now captures three frames around the barcode lock, runs each through a tuned Tesseract worker (PSM 6 + character whitelist), and silently auto-claims if today's date is read off the print. Typed entry is a true fallback now.",
        audience: "everyone",
      },
      {
        type: "fix",
        title: "Coupon popover shows your custom title",
        detail: "If you set a coupon label in Admin (like 'FREE Pizza'), the popover now shows that label as the headline. Percent off is the small kicker.",
        audience: "everyone",
      },
      {
        type: "admin",
        title: "Bug-report email diagnostics",
        detail: "Every ticket-opened email send now logs sent/skipped/error per admin recipient with env-flag context (`resend_key_set`, `admin_email_set`) so missing-email symptoms can be triaged from production logs without a redeploy.",
        audience: "admin",
      },
      {
        type: "admin",
        title: "Passcode claim diagnostic logs",
        detail: "Backend now logs the raw + normalized code on every claim attempt (and explicitly logs LENIENT-regex acceptances). Easier to triage future format mismatches without redeploying.",
        audience: "admin",
      },
    ],
  },
  {
    version: "5.2.36",
    date: "2026-02-14",
    headline: "Blueprint redesign + notifications glow-up",
    items: [
      {
        type: "feature",
        title: "Blueprint redesign on Home + Login",
        detail: "Deep Five Below blue with graph-paper grid, registration marks, monospace technical labels, outlined tiles, and a drawing-block cartouche footer. Inspired by Five Below's brand spec sheets.",
        audience: "everyone",
      },
      {
        type: "feature",
        title: "Frosted glass notifications popover",
        detail: "Notifications now drop down from the bell with a tiny caret pointing back to it. Each card cascades in with a staggered fade. Frosted glass shows the blueprint grid behind.",
        audience: "everyone",
      },
      {
        type: "polish",
        title: "Frosted glass profile drawer",
        detail: "Profile slide-out is semi-transparent white with a 24px blur — the blueprint grid bleeds through subtly.",
        audience: "everyone",
      },
      {
        type: "fix",
        title: "Home page fits on one screen",
        detail: "Tile chevron moved to the top-right corner (white outlined square inside the gradient blob), minHeight reduced 112 → 92, all 7 tiles + Daily Passcode + Stories + footer now fit on iPhone 13 without scrolling.",
        audience: "everyone",
      },
      {
        type: "fix",
        title: "Coupon barcode no longer hides on Collect Emails",
        detail: "The header's backdrop-filter was creating a CSS containing block that anchored the bottom-sheet to the header instead of the viewport (pushed the barcode off-screen). Sheet now renders via React portal to escape any parent containing block.",
        audience: "admin",
      },
    ],
  },
  {
    version: "5.2.35",
    date: "2026-02-14",
    headline: "Daily Passcode + 10% Coupon",
    items: [
      {
        type: "feature",
        title: "Daily Passcode barcode + camera scan",
        detail: "Scan your printed register passcode once, then carry the Code 128 barcode in-app for the rest of your shift. Expires at midnight in store TZ. First-scan-wins so no one can claim it twice.",
        audience: "everyone",
      },
      {
        type: "feature",
        title: "10% cashier coupon",
        detail: "Tap the % pill in the header → bottom sheet pops up with the coupon barcode + optional online promo code. Admin manages the active coupon.",
        audience: "everyone",
      },
      {
        type: "feature",
        title: "@mentions in Stories",
        detail: "Type @ in a story caption to mention a crew member. They get a notification bell + email if configured.",
        audience: "everyone",
      },
      {
        type: "security",
        title: "Daily Passcode hidden in Mirror Mode",
        detail: "When a DM mirrors a sister store, the passcode card is suppressed (passcodes are personal — no leakage into the shadowed view).",
        audience: "admin",
      },
    ],
  },
  {
    version: "5.2.34",
    date: "2026-02-13",
    headline: "Team Chats vs Banner Blasts split",
    items: [
      {
        type: "feature",
        title: "Banner Blasts separated from Team Chats",
        detail: "'Whole Team' inbox threads are now distinct from broadcast Banner Blasts. Messages carry a `delivery_channel` flag so the UI can route them to the right surface.",
        audience: "everyone",
      },
      {
        type: "feature",
        title: "DM Lockout Bypass",
        detail: "District Managers can now bypass Sister-Store lockouts for specific crew members from the Admin console.",
        audience: "admin",
      },
      {
        type: "security",
        title: "CORS wildcard removed + hardcoded manager password deleted",
        detail: "Defense-in-depth pass on the auth surface.",
        audience: "admin",
      },
    ],
  },
  {
    version: "5.2.33",
    date: "2026-02-12",
    headline: "Instants removed + real-time presence fix",
    items: [
      {
        type: "polish",
        title: "Instants feature retired",
        detail: "Removed from UI, routes, and DB models. (Production DB has a leftover `instants` collection — pending Emergent Support cleanup.)",
        audience: "everyone",
      },
      {
        type: "fix",
        title: "Real-time presence dot reliable",
        detail: "Heartbeat now hits `/notifications/unread-count` globally so the green dot doesn't flicker offline when you switch tabs.",
        audience: "admin",
      },
      {
        type: "fix",
        title: "Email Contest tile no longer flashes",
        detail: "Backend PUT saving fixed + frontend gate corrected so the contest tile state is stable on first paint.",
        audience: "everyone",
      },
    ],
  },
];

/** Convenience: most recent version entry. */
export const LATEST = CHANGELOG[0];

/** Items in the latest version visible to crew (drops admin-only items). */
export const LATEST_PUBLIC_ITEMS = LATEST.items.filter((it) => it.audience !== "admin");
